export enum ContentType {
  CDP = "CDP",
  POPULUS = "POPULUS",
}
// eslint-disable-next-line react-refresh/only-export-components
export enum InterestLevel {
  DECLINED = "DECLINED",
  INTERESTED = "INTERESTED",
  NEED_TIME = "NEED_TIME",
}

// eslint-disable-next-line react-refresh/only-export-components
export enum InterestReason {
  NONE = "NONE",
  OTHER = "OTHER",
  INSURANCE = "INSURANCE",
  NEED_MORE_TIME = "NEED_MORE_TIME",
  TRANSPORTATION = "TRANSPORTATION",
  TIME_CONSTRAINT = "TIME_CONSTRAINT",
  DOES_NOT_WANT_TO_KNOW = "DOES_NOT_WANT_TO_KNOW",
  ALREADY_BEING_FOLLOWED = "ALREADY_BEING_FOLLOWED",
  ALREADY_COMPLETED = "ALREADY_COMPLETED",
  PREVIOUS_FAMILY_TESTING = "PREVIOUS_FAMILY_TESTING",
  PREVIOUS_PERSONAL_TESTING = "PREVIOUS_PERSONAL_TESTING",
}

export type PopulusAttributes = {
  zone: string;
  debug: string;
  network: string;
  partner: string;
  keywords: string;
  "visit-id": string;
  "age-range": number;
  "visit-stage": "during";
  "partner-subacct": string | null;
};

export type CDPCampaignImage = {
  url: string;
  height: number;
  width: number;
};

export type CDPCampaign = {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  keywords: string[];
  images: Array<CDPCampaignImage>;
};

// Cancer type enum matching backend values
export type CancerTypeEnum =
  | 'BREAST'
  | 'OVARIAN'
  | 'ENDOMETRIAL_OR_UTERINE'
  | 'PROSTATE'
  | 'COLORECTAL'
  | 'STOMACH'
  | 'PANCREATIC'
  | 'SARCOMA'
  | 'LUNG'
  | 'BRAIN'
  | 'KIDNEY'
  | 'MELANOMA'
  | 'THYROID'
  | 'LIVER'
  | 'BLADDER'
  | 'HEAD_AND_NECK'
  | 'TESTICULAR'
  | 'SEBACEOUS_NEOPLASMS'
  | 'BILIARY_TRACT'
  | 'UVEAL_MELANOMA'
  | 'ESOPHAGEAL_CANCER'
  | 'HIGH_RISK_PROSTATE'
  | 'OTHER';

// Cancer history for a patient
export type Cancer = {
  id: string;
  type: CancerTypeEnum;
  typeName: string;
  ageAtDiagnosis: number | null;
  recurrence: boolean | null;
  tripleNegative: boolean | null;
  bilateral: boolean | null;
  metastatic: boolean | null;
};

// Past genetic test results
export type PastGeneticTest = {
  id: number;
  hadTesting: number | null;
  inLastFiveYears: number | null;
  testResultsKnown: number | null;
  pathogenicMutations: string[];
  pathogenicBiallelicMutations: string[];
  vusMutations: string[];
  familyLynchTesting: number | null;
  familyBrcaTesting: number | null;
};

// Genetic test result from ordered tests
export type GeneticTestResult = {
  geneName: string;
  variantClassification: string | null;
  variantName: string | null;
};

// Demographics
export type Demographic = {
  id: number;
  birthSex: 'MALE' | 'FEMALE' | null;
  ethnicity: string | null;
  race: string | null;
  adopted: boolean | null;
  ashkenaziJewish: boolean | null;
};

// Lifestyle/smoking data
export type Lifestyle = {
  currentlySmokes: boolean | null;
  yearsSmoked: number | null;
  packsPerDay: number | null;
  yearQuitSmoking: number | null;
};

// TC8 risk scores
export type TC8Scores = {
  fiveYear: number | null;
  tenYear: number | null;
  fifteenYear: number | null;
  lifetime: number | null;
  lifetimeCm: number | null;
  brca1: number | null;
  brca2: number | null;
  anyBrca: number | null;
};

export type Patient = {
  address1: string | null;
  address2: string | null;
  city: string | null;
  date_of_birth: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  state: string | null;
  zip_code: string | null;
  preferred_appointment_time: PreferredAppointmentTime;
  screening_appointments_interest: PatientAppointmentsInterest;
  // Aggregate format fields (only present when format=aggregate is requested)
  age?: number | null;
  cancers?: Cancer[];
  pastGeneticTest?: PastGeneticTest | null;
  geneticTestResults?: GeneticTestResult[];
  demographic?: Demographic | null;
  lifestyle?: Lifestyle | null;
  tc8Scores?: TC8Scores | null;
};

export type AdvertContextType = {
  contentType: ContentType | null;
  cdpCampaign: CDPCampaign | null;
  populusAttributes: PopulusAttributes | null;
};

export type PreferredAppointmentTime = {
  id: number;
  availability: {
    anytime: boolean;
    monday?: Array<'AM' | 'PM'>;
    tuesday?: Array<'AM' | 'PM'>;
    wednesday?: Array<'AM' | 'PM'>;
    thursday?: Array<'AM' | 'PM'>;
    friday?: Array<'AM' | 'PM'>;
  };
  startTime: number | null;
  stopTime: number | null;
  createdAt: string;
  updatedAt: string;
}

type InterestData = {
  level: InterestLevel;
  reason?: InterestReason;
};

export type PatientAppointmentsInterest = {
  breast: InterestData | null;
  prostate: InterestData | null;
  cervical: InterestData | null;
  colon: InterestData | null;
  lung: InterestData | null;
  mri: InterestData | null;
  counseling: InterestData | null;
  testing: InterestData | null;
};
